import { useState, useEffect, useRef } from 'react'
import CollectionDataGrid from '@/components/CollectionDataGrid'
import { pbUpsert, usePaginatedCollection, pbAddr } from '@/pb'
import { useInputState } from '@/utils'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Typography,
  IconButton,
  LinearProgress,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'

const Editor = ({ row, onClose }) => {
  const [title, setTitle] = useInputState(row?.title)
  const [author, setAuthor] = useInputState(row?.author)
  const [mediaFile, setMediaFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef(null)

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null
    setMediaFile(file)
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const data = {
        id: row?.id,
        title,
        author,
      }

      if (mediaFile) {
        data.file = mediaFile
      }

      await pbUpsert('music', data)
      onClose()
    } catch (error) {
      console.error('Upload failed:', error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <DialogTitle>Загрузить трек (аудио / видео)</DialogTitle>
      {saving && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
      <DialogContent className="editorForm">
        <TextField label="Название" value={title} onChange={setTitle} fullWidth margin="dense" />
        <TextField label="Автор" value={author} onChange={setAuthor} multiline fullWidth margin="dense" />

        <Box sx={{ mt: 2 }}>
          <input
            type="file"
            accept="audio/*,video/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button variant="outlined" onClick={handleUploadClick} disabled={saving}>
            Выбрать аудио или видео
          </Button>

          {mediaFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Выбран: {mediaFile.name}
            </Typography>
          )}

          {row?.file && !mediaFile && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Текущий файл: {row.file}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Отмена
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !title?.trim()}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </>
  )
}

const getFileUrl = (record, filename) => {
  if (!record?.id || !filename) return null
  return `${pbAddr}/api/files/music/${record.id}/${filename}`
}

export const AudioPlayerCell = ({ value, row }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const mediaRef = useRef(null)
  const mediaUrl = getFileUrl(row, value)

  useEffect(() => {
    return () => {
      if (mediaRef.current) {
        mediaRef.current.pause()
        mediaRef.current = null
      }
    }
  }, [])

  const togglePlay = () => {
    if (!mediaUrl) return

    if (!mediaRef.current) {
      mediaRef.current = new Audio(mediaUrl)
      mediaRef.current.addEventListener('ended', () => setIsPlaying(false))
    }

    if (isPlaying) {
      mediaRef.current.pause()
      setIsPlaying(false)
    } else {
      mediaRef.current.play()
      setIsPlaying(true)
    }
  }

  if (!value) return <span style={{ opacity: 0.5 }}>—</span>

  return (
    <IconButton onClick={togglePlay} size="small" color="primary">
      {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
    </IconButton>
  )
}

const columns = [
  { field: 'title', headerName: 'Название', width: 300 },
  { field: 'author', headerName: 'Автор', width: 300 },
  {
    field: 'file',
    headerName: 'Файл',
    width: 120,
    renderCell: ({ value, row }) => <AudioPlayerCell value={value} row={row} />,
  },
]

export default function Music({ title }) {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const { records: music, totalItems, loaded } = usePaginatedCollection('music', {
    page: paginationModel.page + 1,
    perPage: paginationModel.pageSize,
    sort: '-created',
  })

  return (
    <CollectionDataGrid
      collectionName={title}
      collection={music}
      columns={columns}
      loading={!loaded}
      editor={Editor}
      serverPagination
      rowCount={totalItems}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
    />
  )
}